import { useEffect, useRef, useState } from 'react';

// A spatial interpretation of the supplied photographic K, not a replacement logo.
export const HeroScene = () => {
  const host = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const element = host.current;
    let disposed = false;
    let cleanup = () => {};
    Promise.all([import('three'), import('three/addons/environments/RoomEnvironment.js')]).then(([THREE, { RoomEnvironment }]) => {
      if (disposed) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
      renderer.setClearColor(0x000000, 0);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.domElement.setAttribute('aria-hidden', 'true');
      element.appendChild(renderer.domElement);
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
      camera.position.z = 13;
      const generator = new THREE.PMREMGenerator(renderer);
      const room = new RoomEnvironment();
      const environment = generator.fromScene(room, 0.04);
      scene.environment = environment.texture;
      const group = new THREE.Group();
      scene.add(group);
      const texture = new THREE.TextureLoader().load('/assets/kiraki-mark.webp', () => { if (!disposed) setReady(true); });
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
      const front = new THREE.MeshBasicMaterial({ map: texture, alphaTest: 0.12, toneMapped: false });
      const sides = new THREE.MeshStandardMaterial({ color: '#bc9a5f', metalness: 0.86, roughness: 0.29 });
      const polygons = [
        [[3, 3], [295, 3], [295, 279], [3, 566]],
        [[553, 3], [866, 3], [609, 281], [299, 281]],
        [[298, 284], [606, 284], [292, 638], [3, 638], [3, 571]],
        [[557, 350], [815, 638], [526, 638], [412, 505]],
      ];
      const geometries = polygons.map((points, index) => {
        const shape = new THREE.Shape();
        points.forEach(([x, y], i) => shape[i === 0 ? 'moveTo' : 'lineTo']((x - 435.5) / 100, (323 - y) / 100));
        shape.closePath();
        const geometry = new THREE.ExtrudeGeometry(shape, { depth: 0.23, bevelEnabled: true, bevelThickness: 0.025, bevelSize: 0.028, bevelSegments: 3, steps: 1 });
        const position = geometry.attributes.position;
        const uv = geometry.attributes.uv;
        for (let i = 0; i < position.count; i++) uv.setXY(i, (position.getX(i) * 100 + 435.5) / 871, (position.getY(i) * 100 + 323) / 646);
        uv.needsUpdate = true;
        const mesh = new THREE.Mesh(geometry, [front, sides]);
        mesh.position.z = index * 0.015;
        group.add(mesh);
        return geometry;
      });
      const target = { x: 0.03, y: -0.15 };
      const pointer = event => {
        if (reduced) return;
        const rect = element.getBoundingClientRect();
        target.y = -0.15 + ((event.clientX - rect.left) / rect.width - 0.5) * 0.3;
        target.x = 0.03 + ((event.clientY - rect.top) / rect.height - 0.5) * 0.12;
      };
      const reset = () => { target.x = 0.03; target.y = -0.15; };
      element.addEventListener('pointermove', pointer);
      element.addEventListener('pointerleave', reset);
      const resize = new ResizeObserver(() => {
        const { width, height } = element.getBoundingClientRect();
        if (!width || !height) return;
        renderer.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      });
      resize.observe(element);
      let visible = true;
      const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; });
      observer.observe(element);
      group.rotation.set(0.03, -0.15, -0.025);
      renderer.setAnimationLoop(time => {
        if (!visible || document.hidden) return;
        group.rotation.x += (target.x - group.rotation.x) * 0.04;
        group.rotation.y += (target.y - group.rotation.y) * 0.04;
        group.position.y = reduced ? 0 : Math.sin(time * 0.00065) * 0.075;
        renderer.render(scene, camera);
      });
      cleanup = () => {
        renderer.setAnimationLoop(null);
        resize.disconnect(); observer.disconnect();
        element.removeEventListener('pointermove', pointer);
        element.removeEventListener('pointerleave', reset);
        geometries.forEach(g => g.dispose());
        front.dispose(); sides.dispose(); texture.dispose();
        environment.dispose(); room.dispose(); generator.dispose(); renderer.dispose();
        renderer.domElement.remove();
      };
    }).catch(() => { /* The original logo remains visible if WebGL is unavailable. */ });
    return () => { disposed = true; cleanup(); };
  }, []);

  return <div className={`hero-scene ${ready ? 'scene-ready' : ''}`} ref={host} data-testid="hero-3d-scene" role="img" aria-label="Trodimenzionalni Kiraki znak K s prikazima četiriju djelatnosti"><img className="hero-mark-fallback" src="/assets/kiraki-mark.webp" alt="" aria-hidden="true" width="871" height="646" fetchPriority="high" /></div>;
};